import Cookies from 'js-cookie';
import axios from 'axios';

const fetchUserLocation = async () => {
  try {
    const ipResponse = await fetch('https://api.ipify.org?format=json');
    const ipData = await ipResponse.json();

    const geoResponse = await fetch(`https://ipwho.is/${ipData.ip}`);
    const geoData = await geoResponse.json();

    return {
      ip: geoData.ip || 'Unknown',
      country: geoData.country || 'Unknown',
      city: geoData.city || 'Unknown',
      continent: geoData.continent || 'Unknown'
    };
  } catch (error) {
    console.error('Error fetching user location:', error);
    return {
      ip: 'Unknown',
      country: 'Unknown',
      city: 'Unknown',
      continent: 'Unknown'
    };
  }
};

const generateSessionId = () => {
  let sessionId = Cookies.get('session_id');
  if (!sessionId) {
    sessionId = `session_${new Date().getTime()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    Cookies.set('session_id', sessionId, { expires: 1, sameSite: 'Lax' });
  }
  return sessionId;
};

const isEventTrackingEnabled = () =>
  Cookies.get('cookie_consent') === 'accepted';

export const trackEventView = async (eventId, eventName) => {
  if (!isEventTrackingEnabled()) return;

  const startTime = new Date().getTime();
  Cookies.set('event_start_time', startTime, { sameSite: 'Lax' });

  const userLocation = await fetchUserLocation();
  const eventTrackingData = JSON.parse(
    Cookies.get('event_tracking_data') || '[]'
  );

  eventTrackingData.push({
    eventId,
    eventName,
    action: 'view',
    startTime,
    path: window.location.pathname,
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    ...userLocation
  });

  Cookies.set('event_tracking_data', JSON.stringify(eventTrackingData), {
    expires: 7,
    sameSite: 'Lax'
  });
};

export const trackAddToCart = async (eventId, eventName) => {
  if (!isEventTrackingEnabled()) return;

  const userLocation = await fetchUserLocation();
  const eventTrackingData = JSON.parse(
    Cookies.get('event_tracking_data') || '[]'
  );

  eventTrackingData.push({
    eventId,
    eventName,
    action: 'add_to_cart',
    path: window.location.pathname,
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    ...userLocation
  });

  Cookies.set('event_tracking_data', JSON.stringify(eventTrackingData), {
    expires: 7,
    sameSite: 'Lax'
  });
};

export const trackQuickBuy = async (eventId, eventName) => {
  if (!isEventTrackingEnabled()) return;

  const userLocation = await fetchUserLocation();
  const eventTrackingData = JSON.parse(
    Cookies.get('event_tracking_data') || '[]'
  );

  eventTrackingData.push({
    eventId,
    eventName,
    action: 'quick_buy',
    path: window.location.pathname,
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    ...userLocation
  });

  Cookies.set('event_tracking_data', JSON.stringify(eventTrackingData), {
    expires: 7,
    sameSite: 'Lax'
  });
};

export const trackEventExit = async (eventId, eventName) => {
  if (!isEventTrackingEnabled()) return;

  const startTime = parseInt(Cookies.get('event_start_time'), 10);
  const endTime = new Date().getTime();
  const timeSpent = Math.round((endTime - startTime) / 1000);

  const userLocation = await fetchUserLocation();
  const eventTrackingData = JSON.parse(
    Cookies.get('event_tracking_data') || '[]'
  );

  eventTrackingData.push({
    eventId,
    eventName,
    action: 'page_exit',
    endTime,
    timeSpent,
    path: window.location.pathname,
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    ...userLocation
  });

  Cookies.set('event_tracking_data', JSON.stringify(eventTrackingData), {
    expires: 7,
    sameSite: 'Lax'
  });

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/analytics/event-tracking/track/`,
      eventTrackingData
    );

    if (!response.status === 201) {
      throw new Error('Network response was not ok');
    }
  } catch (error) {
    console.error('Error tracking event exit:', error);
  }
};

export const trackEventCheckout = async (eventId, eventName, cart) => {
  if (!isEventTrackingEnabled()) return;

  let checkoutTrackingData = Cookies.get('checkout_tracking_data');
  if (!checkoutTrackingData) {
    checkoutTrackingData = '[]';
  }
  checkoutTrackingData = JSON.parse(checkoutTrackingData);

  const userLocation = await fetchUserLocation();

  const startTime = parseInt(Cookies.get('event_start_time'), 10);

  const endTime = new Date().getTime();
  const timeSpent = Math.round((endTime - startTime) / 1000);

  checkoutTrackingData.push({
    eventId,
    eventName,
    cart,
    action: 'checkout',
    endTime,
    timeSpent,
    path: window.location.pathname,
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    ...userLocation
  });

  Cookies.set('checkout_tracking_data', JSON.stringify(checkoutTrackingData), {
    expires: 7,
    sameSite: 'Lax'
  });
};

export const trackEventPurchase = async (eventId, eventName, order) => {
  if (!isEventTrackingEnabled()) return;

  let purchaseTrackingData = Cookies.get('purchase_tracking_data');
  if (!purchaseTrackingData) {
    purchaseTrackingData = '[]';
  }
  purchaseTrackingData = JSON.parse(purchaseTrackingData);

  const userLocation = await fetchUserLocation();

  const startTime = parseInt(Cookies.get('event_start_time'), 10);
  const endTime = new Date().getTime();
  const timeSpent = Math.round((endTime - startTime) / 1000);

  purchaseTrackingData.push({
    eventId,
    eventName,
    action: 'payment',
    endTime,
    order,
    timeSpent,
    path: window.location.pathname,
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    ...userLocation
  });

  Cookies.set('payment_tracking_data', JSON.stringify(purchaseTrackingData), {
    expires: 7,
    sameSite: 'Lax'
  });
};

export const trackEventPayment = async (eventId, eventName, order) => {
  if (!isEventTrackingEnabled()) return;

  let paymentTrackingData = Cookies.get('payment_tracking_data');
  if (!paymentTrackingData) {
    paymentTrackingData = '[]';
  }
  paymentTrackingData = JSON.parse(paymentTrackingData);

  const userLocation = await fetchUserLocation();

  const startTime = parseInt(Cookies.get('event_start_time'), 10);
  const endTime = new Date().getTime();
  const timeSpent = Math.round((endTime - startTime) / 1000);

  paymentTrackingData.push({
    eventId,
    eventName,
    action: 'payment',
    endTime,
    order,
    timeSpent,
    path: window.location.pathname,
    sessionId: generateSessionId(),
    timestamp: new Date().toISOString(),
    ...userLocation
  });

  Cookies.set('payment_tracking_data', JSON.stringify(paymentTrackingData), {
    expires: 7,
    sameSite: 'Lax'
  });

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/analytics/event-tracking/track/`,
      paymentTrackingData
    );

    if (!response.status === 201) {
      throw new Error('Network response was not ok');
    }
  } catch (error) {
    console.error('Error tracking event payment:', error);
  }
};
